import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  ViewEntityType,
  ViewUpdateInput,
} from "@colab/shared";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

// PATCH /api/admin/views/:id
//
// Pessimistic — server validates the FilterTree and the permission gate
// (private-creator vs. settings.write). Optimistic name/column/filter
// edits would briefly render a row the server rejects; not worth the
// flicker. Caller passes `entityType` so the invalidation targets the
// right list cache without re-fetching the row.

type Vars = {
  id: string;
  entityType: ViewEntityType;
  patch: ViewUpdateInput;
};

export function useUpdateView() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, Vars>({
    mutationFn: ({ id, patch }) =>
      apiFetch<{ ok: true }>(`/admin/views/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, { entityType }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.views.list(entityType),
      });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to update view");
    },
  });
}
